import {NAV_MENU, NavigationItem} from '../../../shared/constants/navigation.constants';
import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation} from '@angular/core';
import {of, Subscription, Subject} from 'rxjs';
import {ModalService} from '../../../shared/services/modal-service.service';
import {PAGES_CONFIG} from '../../../shared/constants/pages.constants';
import {Router} from '@angular/router';
import {RequestsService} from '../../../requests/services/requests.service';
import {UserService} from '../../../shared/services/user.service';
import {environment} from '../../../../environments/environment';
import {finalize, first, switchMap, takeUntil} from 'rxjs/operators';
import {UserRoles} from '../../../shared/constants/user-roles.constant';
import {EnvService} from '../../../env.service';
import {TranslateService} from '@ngx-translate/core';
import {FormGroup, FormControl} from '@angular/forms';
import {UserSettingsService} from '../../../shared/services/user-settings.service';
import {SystemUserModel, UserTheme} from '../../../shared/models/user.model';
import {UserPermissionsService} from '../../../shared/services/user-permissions.service';

@Component({
  selector: 'app-nav-header-panel',
  templateUrl: './nav-header-panel.component.html',
  styleUrls: ['./nav-header-panel.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class NavHeaderPanelComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject<void>();
  @Input() public loggedIn: boolean;
  @Input() public allowAuthLogout: boolean;
  @Input() public displayName: string;
  @Input() public user: SystemUserModel;
  @Input() public isMimicMode: boolean;
  @Input() public authUserName: string;
  @Output() public loginClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() public logoutClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() public authLogout: EventEmitter<any> = new EventEmitter<any>();
  @Output() public resetMimicUser: EventEmitter<any> = new EventEmitter<any>();

  public isAppsMenuVisible = false;
  public navList: NavigationItem[] = NAV_MENU;
  public buttonLoader = false;
  public initialUserName: string;
  public userName: string;
  public loggedInProduceit: boolean;
  public version: {ui: string; api: string} = {ui: null, api: null};
  public userRoles = UserRoles;
  darkModeForm = new FormGroup({
    enableDarkMode: new FormControl(false)
  });

  private subscriptions: Array<Subscription> = [];
  public get substituteEnabled(): boolean {
    return this.env.substituteEnabled;
  }
  settingsVisible$: Promise<boolean>;
  isPoApprovals = false;

  constructor(
    private modalService: ModalService,
    private router: Router,
    private requestService: RequestsService,
    public env: EnvService,
    private userService: UserService,
    private userSettingsService: UserSettingsService,
    private translateSerivce: TranslateService,
    private userPermissionsService: UserPermissionsService,
  ) {
    this.settingsVisible$ = Promise.all(
      Object.values({
        settings: this.userPermissionsService.isActionAvailable(this.userRoles.SETTINGS.EMAIL)
      })
    ).then(values => values.every(v => !!v));
  }

  ngOnInit(): void {
    this.version.ui = environment.appVersion + '-' + environment.commitId;
    this.initialUserName = this.userService.getCurrentUserName();
    this.userName = this.userService.getCurrentUserName();

    this.router.events.subscribe(event => {
      this.isPoApprovals = this.router.url.indexOf('approvals') !== -1;
    });

    this.userService
      .authAccount$()
      .pipe(switchMap(() => this.requestService.getAPIversion()))
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(apiVersion => {
        this.version.api = apiVersion || this.version.api;
      });

    this.subscriptions.push(
      this.userService.usernameChange().subscribe(value => {
        this.initialUserName = value;
        this.userName = value;
      }),
      this.userService.getLoggedInProduceit().subscribe(loggedIn => {
        this.loggedInProduceit = loggedIn;
      })
    );

    this.userSettingsService.userTheme$.subscribe(theme => {
      if (theme === UserTheme.DARK) {
        this.darkModeForm.get('enableDarkMode').setValue(true, {emitEvent: false});
      } else {
        this.darkModeForm.get('enableDarkMode').setValue(false, {emitEvent: false});
      }
    });
    this.darkModeForm.get('enableDarkMode').valueChanges.subscribe(darkModeEnabled => {
      this.userSettingsService.updateUserTheme(darkModeEnabled ? UserTheme.DARK : UserTheme.LIGHT);
    });
  }

  public createNewRequest(): void {
    this.buttonLoader = true;

    this.requestService
      .createRequest()
      .pipe(
        finalize(() => (this.buttonLoader = false)),
        switchMap(requestTypes => this.modalService.openNewRequestModal(requestTypes)),
        switchMap(({name, type, redirect}) => {
          if (redirect) {
            return of({id: null});
          }
          return this.requestService.postCreateNewRequest(name, type);
        }),
        first()
      )
      .subscribe(({id}) => {
        if (id) {
          this.router.navigate([`/${PAGES_CONFIG.REQUESTS}`, id]);
        }
      });
  }

  ngOnDestroy(): void {
    this.subscriptions?.forEach(sub => sub?.unsubscribe());
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  refreshUserName() {
    this.userService.setCurrentUserName(this.userName);
    window.location.reload();
  }

  logIn() {
    this.loginClicked.emit();
  }

  logOut() {
    this.logoutClicked.emit();
  }

  authLogOut() {
    this.authLogout.emit();
  }

  resetMimic() {
    this.resetMimicUser.emit();
  }

  substitute() {
    this.router.navigate([`/${PAGES_CONFIG.SUBSTITUTES}`]);
  }

  settings(): void {
    this.userSettingsService.openModal();
  }

  getUserInfo() {
    const substitutes = [...(this.user?.substitutedUsers || [])].join(', ');
    const mimicUser = this.initialUserName;

    return this.translateSerivce.instant(
      !substitutes.length
        ? `module.nav-header.user-title${mimicUser !== this.authUserName ? '-as' : ''}`
        : `module.nav-header.user-substituted${mimicUser !== this.authUserName ? '-as' : ''}`,
      {
        displayName: this.displayName,
        mimicUser,
        substitutes
      }
    );
  }
}
